<!-- DeleteConfirmModal.vue -->
<script setup>
import { ref, watch, onMounted, onBeforeUnmount, computed } from "vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show: { type: Boolean, default: false },

  title: { type: String, default: "Confirmar exclusão" },

  // Pode ser string com \n (vou renderizar com pre-line no CSS do message)
  messageTitle: { type: String, default: "Tem certeza que deseja excluir?" },
  messageSubtitle: {
    type: String,
    default: "Esta ação não pode ser desfeita.",
  },

  confirmText: { type: String, default: "Sim, excluir" },
  cancelText: { type: String, default: "Cancelar" },
  loadingText: { type: String, default: "Apagando..." },

  // Função async que executa a exclusão. Se der erro, deve lançar Error(message)
  onConfirm: { type: Function, required: true },

  // Se quiser esconder o ícone vermelho (opcional)
  showDangerIcon: { type: Boolean, default: true },

  // Se quiser mostrar apiError dentro do modal (opcional)
  showInlineError: { type: Boolean, default: false },

  // Se true, fecha o modal assim que onConfirm resolver, sem exibir o toast
  // interno de sucesso — usado quando quem chama já mostra seu próprio toast
  // contextual e não quer os dois sobrepostos.
  closeImmediatelyOnSuccess: { type: Boolean, default: false },

  // Se true, esconde o painel/backdrop do modal assim que onConfirm resolver,
  // mas mantém o toast interno de sucesso visível sozinho (sem o modal atrás)
  // até seu tempo normal terminar, só então emitindo success/close.
  hideModalOnSuccess: { type: Boolean, default: false },
});

const emit = defineEmits(["close", "success"]);

// Estados
const isLoading = ref(false);
const apiError = ref(null);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Este componente não desmonta entre
// aberturas (só alterna `show`), então o watch abaixo recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

// Controla a exibição do painel/backdrop separadamente de `show` — usado por
// hideModalOnSuccess para esconder o modal antes de emitir close/success.
const modalVisible = ref(true);

// Toast
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

function resetToastTimeout(duration) {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, duration);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(6000);
}

function showSuccessToast(message, onFinish) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(2400);

  setTimeout(() => {
    if (typeof onFinish === "function") onFinish();
  }, 2800); // 2400ms visible + 400ms to let the fade-out transition finish before unmounting
}

const subtitleComputed = computed(() => {
  return props.messageSubtitle + (isLoading.value ? " Aguarde..." : "");
});

async function handleConfirm() {
  if (isLoading.value) return;

  isLoading.value = true;
  apiError.value = null;

  try {
    await props.onConfirm();

    if (props.closeImmediatelyOnSuccess) {
      emit("success");
      emit("close");
    } else {
      if (props.hideModalOnSuccess) {
        modalVisible.value = false;
      }

      showSuccessToast("Exclusão realizada com sucesso.", () => {
        emit("success");
        emit("close");
      });
    }
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.title ||
      err?.message ||
      "Ocorreu um erro ao excluir.";

    apiError.value = msg;
    showErrorToast(msg);
    isLoading.value = false;
  }
}

function closeIfAllowed() {
  if (isLoading.value) return;
  emit("close");
}

function handleEscKey(event) {
  if (!props.show) return;
  if (event.key === "Escape" && !isLoading.value) emit("close");
}

onMounted(() => {
  document.addEventListener("keydown", handleEscKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

// Quando o modal abrir/fechar, zera estados (evita “vazar” erro/toast)
watch(
  () => props.show,
  (val) => {
    if (val) {
      isLoading.value = false;
      apiError.value = null;
      showToast.value = false;
      toastMessage.value = "";
      toastType.value = "error";
      modalVisible.value = true;
      resetPosition();
    }
  }
);
</script>

<template>
  <Teleport to="body">
    <div v-if="show && modalVisible" class="modal-backdrop" @click.self="closeIfAllowed">
      <div ref="panelRef" class="modal-panel small" :style="panelStyle">
        <div
          class="modal-header"
          :class="{
 'modal-header--draggable': isDraggable,
            'modal-header--dragging': isDragging,
          }"
          @pointerdown="onHandlePointerDown"
        >
          <h3 class="modal-title">{{ title }}</h3>

          <button
            @click="closeIfAllowed"
            type="button"
            class="modal-close-btn"
            :disabled="isLoading"
            aria-label="Fechar"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="showDangerIcon" class="danger-icon">
            <svg class="w-11 h-11" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <p class="confirm-title">{{ messageTitle }}</p>

          <p class="confirm-subtitle">
            {{ subtitleComputed }}
          </p>

          <p v-if="showInlineError && apiError" class="inline-error">
            {{ apiError }}
          </p>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeIfAllowed"
            :disabled="isLoading"
          >
            {{ cancelText }}
          </button>

          <button
            type="button"
            class="btn btn-danger"
            @click="handleConfirm"
            :disabled="isLoading"
          >
            <span
              v-if="isLoading"
              class="mr-2 inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"
            />
            {{ isLoading ? loadingText : confirmText }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ✅ Toast (igual ao AddAuthorForm.vue) -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[14000] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />

              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />

              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.modal-panel.small {
  max-width: 28rem;
}

.modal-body {
  text-align: center;
}

</style>